"use client";

import { useRef } from "react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { SOC2_COPY, SOC2_STATUS, SUBPROCESSORS, SUBPROCESSORS_FOOTNOTE } from "./data";

const CARD = "rounded-ref-lg border border-line bg-surface shadow-ref";

/** Status pill — amber while in progress, brand pink once certified (never
 *  green), data-blue for the on-request DPA. */
const pill = (color: string, bgPct: number, borderPct: number) => ({
  color,
  background: `color-mix(in srgb, ${color} ${bgPct}%, white)`,
  borderColor: `color-mix(in srgb, ${color} ${borderPct}%, transparent)`,
});

const SOC2_PILL =
  SOC2_STATUS === "certified"
    ? pill("var(--color-brand-600)", 9, 30)
    : pill("var(--color-data-amber)", 12, 35);

const SUBS_COLS = "md:grid-cols-[minmax(110px,150px)_minmax(0,1fr)_minmax(60px,80px)]";

/**
 * Compliance & certifications — status without spin: SOC 2 says "Not
 * certified" until a report exists (data.ts SOC2_STATUS flips the badge and
 * copy together), the DPA is a named artifact, and the subprocessor list is
 * derived from lib/legal/facts.ts — the privacy policy's own list. On the neutral wash band.
 */
export default function ComplianceSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  // stagger 0 — the card stack and the subprocessor table are one visual row.
  useEnterStagger(rootRef, "[data-comp-card]", { y: 26, stagger: 0 });

  const soc2 = SOC2_COPY[SOC2_STATUS];

  return (
    <SectionShell
      id="compliance"
      band="neutral"
      className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px"
    >
      <div ref={rootRef}>
        <SectionHead
          eyebrow="Compliance & certifications"
          title="Status, not spin."
          lead="Not certified means not certified — never hidden, never dressed up as more."
        />

        <div data-grid-block className="grid items-start gap-4 lg:grid-cols-[5fr_7fr]">
          {/* The certification cards. */}
          <div className="flex flex-col gap-4">
            <div data-comp-card className={`${CARD} px-[26px] py-6`}>
              <div className="flex items-center gap-3">
                <span className="text-[15.5px] font-bold">SOC 2</span>
                <span
                  className="ml-auto whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[10px]"
                  style={SOC2_PILL}
                >
                  {soc2.badge}
                </span>
              </div>
              <p className="mt-2 text-[13px] leading-[1.55] text-muted">{soc2.body}</p>
            </div>

            <div data-comp-card className={`${CARD} px-[26px] py-6`}>
              <div className="flex items-center gap-3">
                <span className="text-[15.5px] font-bold">DPA — GDPR / DSGVO</span>
                <span
                  className="ml-auto whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[10px]"
                  style={pill("var(--color-data-blue)", 10, 35)}
                >
                  Via sales
                </span>
              </div>
              <p className="mt-2 text-[13px] leading-[1.55] text-muted">
                Standard template with processor terms and the subprocessor
                list — request from{" "}
                <a href="mailto:sales@rii.link" className="text-brand-600 hover:text-brand-700">
                  sales@rii.link
                </a>
                , countersigned in days.
              </p>
            </div>
          </div>

          {/* Subprocessors — the whole list. */}
          <div data-comp-card className={`${CARD} overflow-hidden`}>
            <div className="border-b border-line bg-surface-2 px-6 py-[13px] font-jetbrains text-[10.5px] uppercase tracking-[0.12em] text-dim">
              Subprocessors — the whole list
            </div>
            <div
              className={`hidden gap-3.5 px-6 pb-2 pt-[11px] font-jetbrains text-[10px] uppercase tracking-[0.09em] text-dim ${SUBS_COLS}`}
            >
              <span>vendor</span>
              <span>purpose</span>
              <span>transfer</span>
            </div>
            {SUBPROCESSORS.map((sub) => (
              <div
                key={sub.key}
                className={`grid items-baseline gap-x-3.5 gap-y-0.5 border-t border-line px-6 py-[13px] ${SUBS_COLS}`}
              >
                <span className="text-[13.5px] font-bold">{sub.name}</span>
                <span className="text-[12.5px] leading-[1.5] text-muted">{sub.purpose}</span>
                <span className="font-jetbrains text-[11px] text-muted">{sub.transfer}</span>
              </div>
            ))}
            <p className="border-t border-line px-6 pb-4 pt-[13px] font-jetbrains text-[10.5px] leading-[1.6] text-dim">
              {SUBPROCESSORS_FOOTNOTE}
            </p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
